//
//  SDKDTMapResolver.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { DTParsedThemeSetPriority, } from "./SDKDTJSONLoader";
import { DTTokenMerger } from "./SDKDTTokenMerger";
import flatMap from "lodash/flatMap";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definitions
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Utility to resolve token maps */
export class DTMapResolver {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token Conversion
    mappedNodePools(map, themes, sets) {
        // Remap for performance
        const setMap = new Map();
        sets.forEach((set) => {
            setMap.set(set.id, set);
        });
        // Resolve which sets we need to use
        const tokenSetToUse = new Array();
        if ((map.pluginSets && map.pluginTheme) ||
            (!map.pluginSets && !map.pluginTheme)) {
            throw new Error(`Either token theme or token set should be defined in the mapping`);
        }
        else if (map.pluginSets) {
            map.pluginSets.forEach((pluginSet) => {
                const set = setMap.get(pluginSet);
                if (!set) {
                    throw new Error(`Mapping references token set '${pluginSet}' that wasn't defined in the sourced data`);
                }
                tokenSetToUse.push(set);
            });
        }
        else if (map.pluginTheme) {
            const pluginTheme = typeof map.pluginTheme === "string"
                ? [map.pluginTheme]
                : map.pluginTheme;
            const theme = themes.filter((t) => pluginTheme.includes(t.id) || pluginTheme.includes(t.name));
            const selectedTokenSets = flatMap(theme.map((t) => t.selectedTokenSets));
            if (!theme || theme.length === 0) {
                throw new Error(`Incorrect plugin theme ${String(map.pluginTheme)}
           referenced by the mapping.\n\nAvailable plugin themes are: [${themes
                    .map((t) => t.name)
                    .join(", ")}
          ]\nYou can also use any of loaded sets: [${sets
                    .map((s) => s.name)
                    .join(", ")}]`);
            }
            // We ignore `disabled` sources, and use both `enabled` and `source` sources, see PF-143
            selectedTokenSets.forEach((pair) => {
                if ([
                    DTParsedThemeSetPriority.source,
                    DTParsedThemeSetPriority.enabled,
                ].includes(pair.priority)) {
                    const set = setMap.get(pair.set.id);
                    if (!set) {
                        throw new Error(`Incorrect plugin set ${pair.set.id}
               referenced by the mapping.\n\nAvailable plugin sets are: [${sets
                            .map((s) => s.name)
                            .join(", ")}
              ]\nYou can also use any of loaded themes: [${themes
                            .map((t) => t.name)
                            .join(", ")}]`);
                    }
                    tokenSetToUse.push(set);
                }
            });
        }
        // Compute unique nodes based on the set combination provided, and retrieve enhanced map
        const uniqueNodes = this.computeUniqueNodesFromSets(tokenSetToUse);
        map.nodes = uniqueNodes;
        return map;
    }
    computeUniqueNodesFromSets(sets) {
        // This method will compute unique nodes based on the created key for the node.
        // If some sets override previous nodes, they'll be overriden, otherwise union is constructed.
        // There can always only be a single path-unique node, the last one applied from all overrides
        const uniqueNodes = new Map();
        sets.forEach((set) => {
            set.contains.forEach((node) => {
                const key = DTTokenMerger.buildKey(node.path, node.name);
                uniqueNodes.set(key, node);
            });
        });
        return Array.from(uniqueNodes.values());
    }
}
